import { useEffect, useRef, useState } from 'react';

import { Button, type ButtonProps } from '../button/button';
import type { IconName } from '../icon/generated_icons';
import { Icon } from '../icon/icon';
import { Tooltip } from '../tooltip/tooltip';
import { TooltipTrigger } from '../tooltip_trigger/tooltip_trigger';

import styles from './copy_button.module.css';

const COPIED_FEEDBACK_DURATION = 2000;

export type CopyButtonProps = Omit<
  ButtonProps,
  'children' | 'icon' | 'onPress' | 'aria-label' | 'aria-labelledby'
> & {
  /** The name of the icon to display in the default state. */
  iconName?: IconName;
  /** The text that is written to the clipboard when the button is pressed. */
  value: string;
  /** Accessible name and tooltip text in the default state. */
  label?: string;
  /** Accessible name and tooltip text while showing copied feedback. */
  copiedLabel?: string;
  /** Called after the value is successfully written to the clipboard. */
  onCopy?: () => void;
  /** Called when writing to the clipboard fails. */
  onCopyError?: (error: unknown) => void;
  /** Duration of the copied feedback animation in milliseconds. */
  animationTimeout?: number;
};

/**
 * Button that copies a value to the clipboard and briefly shows a checkmark to
 * confirm the copy succeeded.
 *
 * ```tsx
 * <CopyButton value={applicationId} label="Copy application ID" />
 * ```
 */
export function CopyButton({
  iconName = 'copy',
  value,
  label = 'Copy to clipboard',
  copiedLabel = 'Copied',
  onCopy,
  onCopyError,
  variant = 'tertiary',
  round = false,
  animationTimeout,
  ...rest
}: CopyButtonProps) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    return () => {
      if (timer.current) {
        clearTimeout(timer.current);
      }
    };
  }, []);

  const handlePress = async () => {
    try {
      await navigator.clipboard.writeText(value);
    } catch (error) {
      onCopyError?.(error);
      return;
    }
    onCopy?.();
    setCopied(true);
    if (timer.current) {
      clearTimeout(timer.current);
    }
    timer.current = setTimeout(() => {
      setCopied(false);
    }, animationTimeout ?? COPIED_FEEDBACK_DURATION);
  };

  const currentLabel = copied ? copiedLabel : label;

  return (
    <TooltipTrigger shouldCloseOnPress={false}>
      <Button
        aria-label={currentLabel}
        variant={variant}
        round={round}
        icon={
          <Icon
            key={copied ? 'checkmark' : iconName}
            name={copied ? 'checkmark' : iconName}
            className={copied ? styles.tick_animation : undefined}
          />
        }
        onPress={handlePress}
        {...rest}
      />
      <Tooltip>{currentLabel}</Tooltip>
    </TooltipTrigger>
  );
}
